/**
 * CartContext — Client-side cart state for the unified checkout.
 *
 * Holds cart items, billing address, and discount code.
 * Not persisted to server — lives in React state, cleared on checkout completion.
 *
 * Usage:
 *   <CartProvider> wraps the app
 *   const { items, addItem, removeItem, clearCart, ... } = useCart();
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [billingAddress, setBillingAddress] = useState(null);
  const [discountCode, setDiscountCode] = useState('');

  const addItem = useCallback((item) => {
    setItems(prev => {
      // Replace if same product+actionType already in cart (can't have two upgrades for same product)
      const existing = prev.findIndex(i =>
        i.productId === item.productId && i.actionType === item.actionType
      );
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = item;
        return updated;
      }
      // Also replace if same product (can only have one action per product)
      const sameProduct = prev.findIndex(i => i.productId === item.productId);
      if (sameProduct >= 0) {
        const updated = [...prev];
        updated[sameProduct] = item;
        return updated;
      }
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setDiscountCode('');
  }, []);

  const updateBillingAddress = useCallback((address) => {
    setBillingAddress(address);
  }, []);

  const itemCount = items.length;
  const hasItems = itemCount > 0;
  const hasPaidItems = items.some(i =>
    i.priceCents > 0 && i.actionType !== 'activate_free'
  );

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      clearCart,
      billingAddress,
      updateBillingAddress,
      discountCode,
      setDiscountCode,
      itemCount,
      hasItems,
      hasPaidItems,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
